/**
 * JS-side mirrors of the canonical theme tokens defined in
 * `src/styles/tokens.css`.
 *
 * Use these constants whenever a hex string is required at runtime — e.g.
 * branding fallbacks that get persisted to the API, or chart fill props
 * that need a literal color. Anything that ends up in a `className` or
 * `style` prop should reference the CSS variables directly via `var(--…)`
 * instead.
 *
 * Keeping a single source means a theme swap is two coordinated edits:
 * this file and `tokens.css`. Components never re-declare colors.
 */

export const THEME = {
  /** Brand surfaces */
  bg: '#0A0A0A',
  bgDeep: '#000000',
  surface: '#141414',
  surface2: '#1C1C1C',
  border: '#2A2A2A',

  /** Brand accent (Xotski purple — sub-brand accent per PRD §23) */
  accent: '#B262F5',
  accentHover: '#C685FF',
  accentDim: '#8D4DD1',

  /** Xotski lime — primary CTA + success per PRD §23 */
  lime: '#C8E530',
  limeHover: '#D6F04A',
  limeDim: '#A8C020',

  /** Brand foreground */
  lavender: '#F5F5F3',
  textMuted: '#A8A8A3',
  textSubtle: '#6B6B66',

  /** Navy backdrop family (auth, onboarding, modals) */
  navy: '#1A1A2E',
  navy2: '#2A2A4E',
  navyMid: '#0F0F1F',

  /** Status palette */
  success: '#4DD28B',
  warn: '#F4B740',
  error: '#FF5A4F',
  info: '#5AA9FF',
} as const;

/**
 * Defaults used as fallbacks when a tenant brand kit hasn't customized a
 * field yet. These reflect the canonical Xotski brand and stay in sync with
 * `--color-brand-default-*` in tokens.css.
 */
export const BRAND_DEFAULTS = {
  primaryColor: THEME.navy,
  secondaryColor: THEME.accent,
  accentColor: THEME.accent,
  backgroundColor: THEME.bg,
  fontColor: THEME.lavender,
  font: 'Inter',
} as const;
